import { useStyles } from '../../../styles/NewOrUpdateForm.styles';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import { Grid, FormControl, TextField } from '@mui/material';
import { isEqual } from 'lodash';

import { type SyntheticEvent, useState } from 'react';
import type { FormComponentProps } from '@components/forms/types';

type SingleSelectProps<State extends Record<string, any>> = FormComponentProps<State> & {
    options: { name: string; id: string }[];
    label: string;
    nameKey: string & keyof State;
    idKey: string & keyof State;
    error?: boolean;
    onChange?: (_: SyntheticEvent<Element, Event>, value: any) => void;
    disable?: boolean;
    size?: number;
    includeAll?: boolean;
};

export const FormSingleSelect = <State extends Record<string, any>>({
    options,
    label,
    onChange,
    error,
    idKey,
    nameKey,
    draft,
    disable,
    size,
    includeAll = true,
}: SingleSelectProps<State>) => {
    const classes = useStyles();
    const [draftState, setDraftState] = draft;
    const [open, setOpen] = useState<boolean>(false);
    const allOption = includeAll ? [{ id: '*', name: 'All ' }, ...options] : options;

    const handleOpen = () => {
        if (disable === true) {
            return;
        }

        setOpen(true);
    };

    return (
        <Grid item md={size ?? 6} xs={12} className={classes.textInputWrapper}>
            <FormControl variant='outlined' className={classes.textInput} error={error} disabled={disable}>
                <Autocomplete
                    open={open}
                    multiple
                    openOnFocus
                    disabled={disable}
                    value={
                        allOption?.filter(({ id }) => {
                            const exp =
                                typeof draftState?.[idKey] === 'number'
                                    ? parseInt(id) === draftState?.[idKey]
                                    : id === draftState?.[idKey];

                            return exp;
                        }) || []
                    }
                    options={allOption || []}
                    getOptionLabel={({ name }) => name}
                    filterSelectedOptions={true}
                    onChange={
                        (onChange ??= (_: SyntheticEvent<Element, Event>, value) => {
                            const { id, name } = value.pop() ?? {};

                            const keysToSet =
                                idKey === nameKey
                                    ? {
                                          ...draftState,
                                          [nameKey]: name,
                                      }
                                    : {
                                          ...draftState,
                                          [idKey]: parseInt(id) ? parseInt(id) : id,
                                          [nameKey]: name,
                                      };

                            setDraftState({
                                ...keysToSet,
                            });
                        })
                    }
                    onOpen={handleOpen}
                    onClose={() => {
                        setOpen(false);
                    }}
                    renderInput={(params) => (
                        <TextField {...params} label={label} variant='outlined' placeholder={`Select ${label}`} />
                    )}
                    ChipProps={{
                        sx: {
                            pointerEvents: disable ? 'none' : 'auto',
                        },
                    }}
                    renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                            {option.name}
                        </li>
                    )}
                    renderTags={(selected, getTagProps) =>
                        selected.map((value, index) => (
                            <Chip
                                size='small'
                                label={value.name}
                                {...getTagProps({ index })}
                                key={index}
                                color='primary'
                                disabled={disable}
                                onClick={(e) => {
                                    if (disable) e.stopPropagation();
                                }}
                            />
                        ))
                    }
                    isOptionEqualToValue={(option, value) => isEqual(option, value)}
                />
            </FormControl>
        </Grid>
    );
};
